export type QaStatus = "pass" | "partial" | "fail" | "blocked" | "not_run";
export type QaPriority = "P0" | "P1" | "P2" | "P3";

export type QaModuleReview = {
  module: string;
  assignedArea: string;
  completion: QaStatus;
  testResult: QaStatus;
  priority: QaPriority;
  lastTestDate: string;
  working: string[];
  missing: string[];
  broken: string[];
  notes: string;
};

export type QaScenarioGroup = {
  title: string;
  items: string[];
};

export const qaLastReviewDate = "2026-05-04";

export const qaScenarioGroups: QaScenarioGroup[] = [
  {
    title: "Visitor / Trial Flow",
    items: [
      "Visitor opens homepage.",
      "Visitor changes language FR/EN.",
      "Visitor views packages.",
      "Visitor creates trial account.",
      "Visitor verifies email.",
      "Trial user logs in.",
      "Trial user sees only trial-allowed features.",
      "Trial user cannot publish live.",
      "Trial user cannot exceed article/site limits.",
      "Trial user sees upgrade CTA.",
    ],
  },
  {
    title: "Subscription Flow",
    items: [
      "User selects package from homepage.",
      "User selects package from control panel.",
      "User reviews package details.",
      "User proceeds through guided checkout steps.",
      "User pays with Stripe test card.",
      "Stripe webhook updates subscription.",
      "Invoice appears in user billing page.",
      "Package permissions are activated.",
      "User can access only features included in package.",
    ],
  },
  {
    title: "Admin Flow",
    items: [
      "Admin views all users.",
      "Admin creates user.",
      "Admin edits user.",
      "Admin assigns package.",
      "Admin changes user role.",
      "Admin views subscriber status.",
      "Admin views all invoices.",
      "Admin views all user data with owner labels.",
      "Admin checks audit log and timeline.",
      "Admin uses login as user.",
      "Admin exits impersonation mode.",
    ],
  },
  {
    title: "Templates / Info Flow",
    items: [
      "Admin creates template.",
      "Admin edits template.",
      "Admin previews template.",
      "User sees only allowed templates.",
      "User completes info page.",
      "User selects package/template/language/domain/category.",
      "Info data is saved and connected to checkout or website creation.",
    ],
  },
  {
    title: "Permission / Security Flow",
    items: [
      "Regular user cannot access admin routes.",
      "Regular user cannot see other users data.",
      "Regular user cannot call restricted Edge Functions.",
      "Package limits are enforced server-side.",
      "Suspended/unpaid user loses paid access.",
      "Admin-only operations require admin role.",
    ],
  },
  {
    title: "Localization Flow",
    items: [
      "French is default.",
      "English can be selected.",
      "User preference persists.",
      "Homepage and Admin pages update language correctly.",
      "No hardcoded untranslated labels remain in main UI.",
    ],
  },
];

export const qaModuleReviews: QaModuleReview[] = [
  {
    module: "Auth",
    assignedArea: "src/pages/AuthPage.tsx, Supabase Auth, verification Edge Functions",
    completion: "partial",
    testResult: "partial",
    priority: "P0",
    lastTestDate: qaLastReviewDate,
    working: ["Login and registration UI exists.", "Registration captures name and phone.", "Verification-code UI is wired."],
    missing: ["Trial subscription creation after verification still depends on Edge Function/database trigger behavior."],
    broken: ["Verification Edge Function availability was not live-verified in this local pass."],
    notes: "Keep registration local-first but deploy and verify send-verification-code/verify-email-code before release.",
  },
  {
    module: "Trial",
    assignedArea: "packages seed, packageAccess, billing pages",
    completion: "partial",
    testResult: "partial",
    priority: "P0",
    lastTestDate: qaLastReviewDate,
    working: ["Trial package is seeded with low limits.", "Sidebar hides package-locked features.", "Trial marketing appears on homepage."],
    missing: ["Server-side article/site limit checks need one shared RPC/Edge Function gate."],
    broken: [],
    notes: "Client-side restrictions are useful for UX, but limit enforcement must be duplicated server-side.",
  },
  {
    module: "Packages",
    assignedArea: "AdminPackagesPage, package seeds, homepage cards",
    completion: "pass",
    testResult: "pass",
    priority: "P1",
    lastTestDate: qaLastReviewDate,
    working: ["Public multilingual package catalog exists.", "Admin package editor saves limits, Stripe IDs, and translations.", "Homepage loads active public packages."],
    missing: ["Live schema must include the new public marketing columns before publish."],
    broken: [],
    notes: "Build passed with package pages included.",
  },
  {
    module: "Services",
    assignedArea: "AdminServicesPage, service_definitions, package_services",
    completion: "pass",
    testResult: "pass",
    priority: "P1",
    lastTestDate: qaLastReviewDate,
    working: ["Admin services page exists.", "Services can be created and edited.", "Seed catalog links services to package slugs."],
    missing: ["Package-service editor is basic and does not yet expose per-package limits in a matrix."],
    broken: [],
    notes: "This closes the previously empty services-management surface.",
  },
  {
    module: "Stripe",
    assignedArea: "create-checkout-session, stripe-webhook, checkout pages",
    completion: "partial",
    testResult: "partial",
    priority: "P0",
    lastTestDate: qaLastReviewDate,
    working: ["Guided checkout pages exist.", "Checkout invokes create-checkout-session.", "Webhook function is present in the repo."],
    missing: ["End-to-end Stripe test-card flow and webhook delivery were not live-run in this local audit."],
    broken: ["Enterprise and trial activation avoid Stripe but need explicit audit entries."],
    notes: "Requires Supabase function deployment and Stripe CLI/webhook test before release.",
  },
  {
    module: "Invoices",
    assignedArea: "BillingInvoicesPage, AdminInvoicesPage, invoices table",
    completion: "pass",
    testResult: "pass",
    priority: "P1",
    lastTestDate: qaLastReviewDate,
    working: ["User and admin invoice pages load invoice rows.", "Seed invoice is included for demo data.", "Status badges cover paid/open/failed/cancelled/refunded states."],
    missing: ["Invoice PDF links depend on webhook payloads."],
    broken: [],
    notes: "Local build validates the invoice pages.",
  },
  {
    module: "Subscribers",
    assignedArea: "AdminSubscribersPage, user_subscriptions",
    completion: "partial",
    testResult: "partial",
    priority: "P1",
    lastTestDate: qaLastReviewDate,
    working: ["Subscriber list page exists and loads subscriptions.", "Admin user rows include subscription status and package labels."],
    missing: ["Manual subscriber package assignment should be exposed from the user detail page and audited."],
    broken: [],
    notes: "Data model exists, but admin workflow still needs a tighter package assignment action.",
  },
  {
    module: "Templates",
    assignedArea: "TemplatesPage, TemplateFormPage, TemplatePreviewPage, site_templates",
    completion: "pass",
    testResult: "pass",
    priority: "P1",
    lastTestDate: qaLastReviewDate,
    working: ["Templates list is no longer empty when seeds are applied.", "Admin create/edit/preview routes exist.", "User list is filtered by package access."],
    missing: ["Template previews are static metadata previews, not full rendered websites."],
    broken: [],
    notes: "RLS and client access both use package/service requirements.",
  },
  {
    module: "Info page",
    assignedArea: "InfoPage, project_intakes",
    completion: "pass",
    testResult: "pass",
    priority: "P1",
    lastTestDate: qaLastReviewDate,
    working: ["Info page saves project intake data.", "Package/template/language/domain/category fields are captured.", "Checkout handoff stores package/template context."],
    missing: ["Website creation wizard does not yet consume the latest intake automatically."],
    broken: [],
    notes: "The form path is usable and connected to checkout review.",
  },
  {
    module: "Homepage",
    assignedArea: "HomePage, public package display, i18n",
    completion: "pass",
    testResult: "pass",
    priority: "P2",
    lastTestDate: qaLastReviewDate,
    working: ["Homepage package display is data-driven.", "FR/EN package names, descriptions, and CTA labels are read from database fields.", "Loading, error, and empty states exist."],
    missing: ["Public stats still depend on availability of public views."],
    broken: [],
    notes: "No fake numbers are shown when public stats are unavailable.",
  },
  {
    module: "User management",
    assignedArea: "TeamsPage, UserFormPage, UserDetailPage, admin-user-actions",
    completion: "partial",
    testResult: "partial",
    priority: "P0",
    lastTestDate: qaLastReviewDate,
    working: ["Admin can view profile/auth/email aggregates.", "Create/edit/detail routes exist.", "Schema-aware fallbacks avoid missing profile columns."],
    missing: ["Email lookup and secure updates require deployed Edge Functions for non-self users."],
    broken: [],
    notes: "Keep admin-user-actions/list-team-user-emails deployed before acceptance testing.",
  },
  {
    module: "Roles",
    assignedArea: "user_roles, is_admin RPC, admin route guard",
    completion: "pass",
    testResult: "pass",
    priority: "P0",
    lastTestDate: qaLastReviewDate,
    working: ["Sidebar hides admin routes for non-admin users.", "Admin route guard now blocks direct /admin/* navigation.", "super_admin is treated as admin by helper fallback."],
    missing: [],
    broken: [],
    notes: "This audit added route-level admin protection on top of sidebar filtering.",
  },
  {
    module: "Audit logs",
    assignedArea: "AdminAuditLogsPage, audit_logs, user_activity_logs",
    completion: "partial",
    testResult: "partial",
    priority: "P1",
    lastTestDate: qaLastReviewDate,
    working: ["Audit log page exists with filters.", "Seed migration inserts audit and activity events."],
    missing: ["Every admin mutation does not yet write a durable audit row.", "Timeline should merge audit_logs and user_activity_logs consistently."],
    broken: [],
    notes: "Audit visibility exists; coverage of all events is the remaining gap.",
  },
  {
    module: "Timeline",
    assignedArea: "UserDetailPage, user_activity_logs",
    completion: "partial",
    testResult: "partial",
    priority: "P2",
    lastTestDate: qaLastReviewDate,
    working: ["User detail page includes timeline-related data hooks.", "Seed migration includes a user_activity_logs event."],
    missing: ["Timeline needs broader event taxonomy and owner labels across resources."],
    broken: [],
    notes: "Useful for QA, but not yet complete enough as a compliance trail.",
  },
  {
    module: "Localization",
    assignedArea: "src/i18n, PublicLayout, SettingsPage, sidebar",
    completion: "partial",
    testResult: "partial",
    priority: "P2",
    lastTestDate: qaLastReviewDate,
    working: ["FR/EN dictionaries exist.", "Language switcher persists user preference.", "Homepage and main nav use translation keys."],
    missing: ["Several admin/billing/template pages still have hardcoded labels."],
    broken: [],
    notes: "French is default, but admin localization is not complete enough to mark done.",
  },
  {
    module: "Permissions",
    assignedArea: "packageAccess, RLS policies, sidebar, admin guard",
    completion: "partial",
    testResult: "partial",
    priority: "P0",
    lastTestDate: qaLastReviewDate,
    working: ["Package-based sidebar restrictions exist.", "Template/service RLS policies are included.", "Admin routes now require admin role."],
    missing: ["Article creation, publishing, media upload, and website creation limits need server-side enforcement hooks."],
    broken: [],
    notes: "Do not rely only on hidden navigation for package limits.",
  },
  {
    module: "Test data",
    assignedArea: "Supabase seed migrations, QA report",
    completion: "partial",
    testResult: "partial",
    priority: "P1",
    lastTestDate: qaLastReviewDate,
    working: ["Packages, services, templates, invoice, subscription, website, article, media, audit, and timeline demo rows are seeded around the first auth user."],
    missing: ["True trial/paid/admin persona accounts cannot be safely created as Auth users from this frontend migration alone."],
    broken: [],
    notes: "Use the QA report personas with Supabase Auth/admin tooling for full live test accounts.",
  },
];

